// React Hooks for Protocol-Process Mapping
// Part of Policy Framework & EU Diseases Implementation

import { useState, useEffect } from 'react';
import protocolProcessMappingService from '../services/protocolProcessMappingService';

/**
 * Hook to fetch mappings
 */
export function useProtocolProcessMappings(organizationId, filters = {}) {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadMappings = async () => {
    if (!organizationId) {
      setMappings([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await protocolProcessMappingService.getMappings(
        organizationId,
        filters
      );
      setMappings(data);
    } catch (err) {
      console.error('Error loading mappings:', err);
      setError(err.message);
      setMappings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMappings();
  }, [organizationId, JSON.stringify(filters)]);

  const refresh = () => {
    loadMappings();
  };

  return { mappings, loading, error, refresh };
}

/**
 * Hook for mapping mutations
 */
export function useProtocolProcessMappingMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createMapping = async (data, userId, userName) => {
    try {
      setLoading(true);
      setError(null);
      const id = await protocolProcessMappingService.createMapping(
        data,
        userId,
        userName
      );
      return id;
    } catch (err) {
      console.error('Error creating mapping:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateMapping = async (mappingId, updates, userId, userName) => {
    try {
      setLoading(true);
      setError(null);
      await protocolProcessMappingService.updateMapping(
        mappingId,
        updates,
        userId,
        userName
      );
    } catch (err) {
      console.error('Error updating mapping:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteMapping = async (mappingId) => {
    try {
      setLoading(true);
      setError(null);
      await protocolProcessMappingService.deleteMapping(mappingId);
    } catch (err) {
      console.error('Error deleting mapping:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    createMapping,
    updateMapping,
    deleteMapping,
    loading,
    error
  };
}

